/**
 * M2 onboarding: wizard state (current step + form data) with localStorage persistence.
 * Pre-org-creation flow; no DynamoDB yet.
 *
 * FB-005: Storage key is now scoped per Clerk user ID to prevent cross-user data
 * pollution. On load, if the stored draft belongs to a different user, it is cleared
 * synchronously BEFORE any state is returned — no stale enrichment data can leak.
 */

import { useState, useEffect, useCallback } from 'react';

/** Legacy key (pre-FB-005) — checked on load for migration, then removed. */
const LEGACY_STORAGE_KEY = 'charitystack_onboarding_draft';

/**
 * Per-user storage key. Exported so CreateFundraiserDrawer can read the draft
 * after redirect for activation form prefill (consume-once pattern).
 */
export function getOnboardingDraftKey(clerkUserId, flowMode = 'initial') {
  const flowSuffix = flowMode === 'create-organization' ? '_create_organization' : '';
  if (!clerkUserId) return LEGACY_STORAGE_KEY;
  return `charitystack_onboarding_draft_${clerkUserId}${flowSuffix}`;
}

/** @deprecated Use getOnboardingDraftKey(userId) instead. Kept for backward compat with draft consumers. */
export const ONBOARDING_DRAFT_STORAGE_KEY = LEGACY_STORAGE_KEY;

/** Entity types — must match backend constants.py ENTITY_TYPE_* */
export const ENTITY_TYPE_NONPROFIT = 'nonprofit';
export const ENTITY_TYPE_INDIVIDUAL = 'individual';

const defaultState = {
  step: 1,
  clerkUserId: '', // track which user owns this draft
  entityType: ENTITY_TYPE_NONPROFIT, // 'nonprofit' | 'individual'
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  orgName: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
  hasWebsite: false,
  websiteUrl: '',
  isMigrating: false,
  // Step 3
  ein: '',
  legalName: '',
  city: '',
  state: '',
  nteeCode: '',
  causeCategory: '',
  einLookupStatus: 'idle', // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  einLookupPayload: null,
  isTaxExempt: true, // true | false — "Does your organization have tax-exempt status?"
  // Website enrichment
  ogImage: '',
  ogDescription: '',
  description: '',
  themeColor: '',
  favicon: '',
  websiteEnrichStatus: 'idle', // 'idle' | 'loading' | 'ok' | 'error'
  suggestedBrandColor: '', // from website-enrich; never overwrites user's brandColor
  // Overrides (user-edited)
  logoUrl: '',
  userUploadedLogoUrl: '', // persists so user can switch back to their upload
  brandColor: '',
  // Org size (annual revenue band)
  annualRevenue: '',
  // A sidebar-launched organization creation must be independently replayable.
  // It is set synchronously when the clean flow draft is constructed.
  creationRequestId: '',
};

function createRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `org-create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getDefaultState(flowMode = 'initial') {
  return {
    ...defaultState,
    flowMode,
    ...(flowMode === 'create-organization' ? { creationRequestId: createRequestId() } : {}),
  };
}

/**
 * Load draft from localStorage. If a clerkUserId is provided, uses the per-user
 * key and migrates any legacy draft that matches the same user.
 */
function loadState(clerkUserId, flowMode = 'initial') {
  const key = getOnboardingDraftKey(clerkUserId, flowMode);
  try {
    // Try per-user key first
    let raw = localStorage.getItem(key);

    // FB-005: Migrate legacy draft if it belongs to this user (or has no user stamp)
    if (!raw && clerkUserId && flowMode === 'initial') {
      const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacyRaw) {
        try {
          const legacyParsed = JSON.parse(legacyRaw);
          if (!legacyParsed.clerkUserId || legacyParsed.clerkUserId === clerkUserId) {
            // Migrate: move to per-user key, remove legacy
            localStorage.setItem(key, legacyRaw);
            localStorage.removeItem(LEGACY_STORAGE_KEY);
            raw = legacyRaw;
          } else {
            // Legacy draft belongs to a different user — delete it, don't migrate
            localStorage.removeItem(LEGACY_STORAGE_KEY);
          }
        } catch {
          localStorage.removeItem(LEGACY_STORAGE_KEY);
        }
      }
    }

    if (!raw) return getDefaultState(flowMode);
    const parsed = JSON.parse(raw);

    // FB-005: If the stored draft's clerkUserId doesn't match the current user,
    // wipe it synchronously. This is the primary guard against cross-user pollution.
    if (clerkUserId && parsed.clerkUserId && parsed.clerkUserId !== clerkUserId) {
      console.log('[useOnboardingState] Draft belongs to different user — clearing synchronously');
      localStorage.removeItem(key);
      return getDefaultState(flowMode);
    }

    const defaults = getDefaultState(flowMode);
    // A draft written before flow modes existed must never change the mode of
    // the route that is currently rendering. In particular, retain the stable
    // request id for a sidebar-created organization across refreshes/retries.
    return {
      ...defaults,
      ...parsed,
      flowMode,
      creationRequestId:
        flowMode === 'create-organization'
          ? parsed.creationRequestId || defaults.creationRequestId
          : '',
    };
  } catch {
    return getDefaultState(flowMode);
  }
}

function saveState(state, clerkUserId, flowMode = 'initial') {
  const key = getOnboardingDraftKey(clerkUserId || state.clerkUserId, flowMode);
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch (e) {
    console.warn('Onboarding state save failed', e);
  }
}

/**
 * @param {string} [clerkUserId] - Current Clerk user ID. When provided, the draft
 *   is scoped per-user and cross-user drafts are cleared synchronously on load.
 */
export function useOnboardingState(clerkUserId, flowMode = 'initial') {
  const [state, setState] = useState(() => loadState(clerkUserId, flowMode));

  // FB-005: If clerkUserId changes (e.g. user switches accounts), reload the
  // correct per-user draft or clear stale data synchronously.
  useEffect(() => {
    if (!clerkUserId) return;
    setState((prev) => {
      if (prev.clerkUserId === clerkUserId && prev.flowMode === flowMode) return prev;
      return loadState(clerkUserId, flowMode);
    });
  }, [clerkUserId, flowMode]);

  useEffect(() => {
    saveState(state, clerkUserId, flowMode);
  }, [state, clerkUserId, flowMode]);

  const update = useCallback((partial) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const setStep = useCallback((step) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  const reset = useCallback(() => {
    setState(getDefaultState(flowMode));
    const key = getOnboardingDraftKey(clerkUserId, flowMode);
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in private browsing; in-memory reset still applies.
    }
  }, [clerkUserId, flowMode]);

  return { state, update, setStep, reset };
}
